import type { Echo, EchoPopularityStats } from '@/types';
import type { FilterOption } from '@/types/filters';

/** Popularity filter mode IDs matching `filterConstants.ts` option IDs. */
export type PopularityFilter = 'trending' | 'most_popular' | 'new_echoes';

/** Display labels for each popularity filter mode. */
export const POPULARITY_LABELS: Record<PopularityFilter, string> = {
  trending: 'Trending',
  most_popular: 'Most Popular',
  new_echoes: 'New Echoes',
};

/**
 * Sorts/filters echoes by the active popularity mode.
 * @param echoes - pre-filtered echo list
 * @param filter - active popularity option id
 * @param popularityMap - per-echo engagement stats
 * @param trendingIds - ordered echo IDs from the trending endpoint
 * @returns sorted (and possibly sliced) echo array
 */
export const sortEchoesByPopularity = (
  echoes: Echo[],
  filter: PopularityFilter,
  popularityMap: Map<string, EchoPopularityStats>,
  trendingIds: string[]
): Echo[] => {
  switch (filter) {
    case 'trending': {
      if (trendingIds.length === 0) return echoes;
      const orderMap = new Map(trendingIds.map((id, idx) => [id, idx]));
      return echoes
        .filter((echo) => orderMap.has(echo.id))
        .sort((left, right) => (orderMap.get(left.id) ?? 0) - (orderMap.get(right.id) ?? 0));
    }
    case 'most_popular':
      return [...echoes].sort((left, right) => {
        const msgsLeft = popularityMap.get(left.id)?.totalMessages ?? 0;
        const msgsRight = popularityMap.get(right.id)?.totalMessages ?? 0;
        return msgsRight - msgsLeft;
      });
    case 'new_echoes':
      return [...echoes]
        .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''))
        .slice(0, 10);
    default:
      return echoes;
  }
};

/**
 * Determines the domain of an Echo from its API domain field.
 */
export const getEchoDomain = (echo: Echo): string => {
  const domain = (echo.domain ?? '').trim();
  if (domain) return domain;
  return 'Other';
};

/**
 * Converts an echo name to a URL-friendly slug.
 * e.g. "Serena Williams" → "serena-williams"
 */
export const toEchoSlug = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

/**
 * Builds a bidirectional slug map for a set of echoes.
 * Duplicate names get a numeric suffix: "gigea-marcel", "gigea-marcel-2", etc.
 * Returns { idToSlug, slugToId } for O(1) lookups.
 */
export const buildEchoSlugMap = (
  echoes: Record<string, Echo>
): { idToSlug: Record<string, string>; slugToId: Record<string, string> } => {
  const idToSlug: Record<string, string> = {};
  const slugToId: Record<string, string> = {};
  const slugCount: Record<string, number> = {};

  // Sort by created_at so the earliest echo keeps the clean slug
  const sorted = Object.entries(echoes).sort(([, left], [, right]) => {
    const dateA = (left as Echo & { created_at?: string }).created_at ?? '';
    const dateB = (right as Echo & { created_at?: string }).created_at ?? '';
    return dateA.localeCompare(dateB);
  });

  for (const [id, echo] of sorted) {
    const baseSlug = toEchoSlug(echo.name);
    const count = (slugCount[baseSlug] ?? 0) + 1;
    slugCount[baseSlug] = count;

    const slug = count === 1 ? baseSlug : `${baseSlug}-${count}`;
    idToSlug[id] = slug;
    slugToId[slug] = id;
  }

  return { idToSlug, slugToId };
};

/**
 * Extracts unique domains from a list of Echoes and formats them for the filter component.
 *
 * @param echoes - The list of Echo objects.
 * @returns Array of FilterOption objects sorted alphabetically.
 */
export const getUniqueDomains = (echoes: Echo[]): FilterOption[] => {
  const domains = echoes
    .map((echo) => getEchoDomain(echo))
    .filter((domain): domain is string => Boolean(domain) && domain.trim() !== '');

  const uniqueDomains = Array.from(new Set(domains));

  return uniqueDomains
    .map((domain) => ({
      id: domain,
      label: domain,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};
